import parsePhoneNumberFromString from 'libphonenumber-js';

import { combineErrors } from './combineErrors';
import { ErrorType, Validator } from './types';

const ERROR_REQUIRED_FIELD = 'Обязательное поле';
const ERROR_INCORRECT_FIELD = 'Допустимы только буквы русского алфавита';
const ERROR_INCORRECT_URL = 'Некорректный URL';
const ERROR_INCORRECT_EMAIL = 'Некорректный Email';
const ERROR_MAX_LENGTH = 'Превышена максимальная длина';
const ERROR_PHONE = 'Некорректный номер телефона';
const ERROR_ONLY_DIGITS = 'Допустимы только цифры';
const ERROR_ONLY_LETTERS = 'Допустимы только буквы русского алфавита';
const ERROR_INN = 'Некорректный инн';
const ERROR_PASSWORD = 'Пароль должен содержать минимум 8 символов, буквы и цифры';

export const getEmptyError = (): ErrorType => {
  return { value: false };
};

export const getError = (text: string): ErrorType => {
  return { value: true, text };
};

export const validateNotEmptyArray =
  <T>(error: string = ERROR_REQUIRED_FIELD): Validator<T[]> =>
  (value: T[]): ErrorType =>
    !value.length ? getError(error) : getEmptyError();

export const validateNotEmpty =
  <T>(error: string = ERROR_REQUIRED_FIELD): Validator<T> =>
  (value?: T | null): ErrorType =>
    value === undefined || value === null || !String(value).trim().length ? getError(error) : getEmptyError();

export const validateMinLength =
  <T>(minLength: number, error: string = `Минимум ${minLength} символов`): Validator<T> =>
  (value?: T | null): ErrorType =>
    value === undefined || value === null || String(value).trim().length < minLength
      ? getError(error)
      : getEmptyError();

export const validateMaxLength =
  <T>(maxLength: number, error: string = ERROR_MAX_LENGTH): Validator<T> =>
  (value?: T | null): ErrorType =>
    value === undefined || value === null || String(value).trim().length > maxLength
      ? getError(error)
      : getEmptyError();

export const validateEmail =
  <T>(errorText: string = ERROR_INCORRECT_EMAIL): Validator<T> =>
  (v?: T | null): ErrorType =>
    !/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(String(v)) ? getError(errorText) : getEmptyError();

export const validatePassword =
  <T>(errorText: string = ERROR_PASSWORD): Validator<T> =>
  (v?: T | null): ErrorType => {
    const password = String(v ?? '');
    const hasMinLength = password.length >= 8;
    const hasLetter = /[a-zA-Zа-яА-ЯёЁ]/.test(password);
    const hasDigit = /\d/.test(password);

    return hasMinLength && hasLetter && hasDigit ? getEmptyError() : getError(errorText);
  };

export const validateChecked =
  (error: string = 'Обязательное поле'): Validator<boolean> =>
  (value?: boolean | null): ErrorType =>
    value === true ? getEmptyError() : getError(error);

/** Валидирует через все валидаторы и возвращает все ошибки */
export const composeValidators =
  <V>(...funcs: Array<Validator<V>>): Validator<V> =>
  (v: V): ErrorType =>
    funcs.reduce<ErrorType>((acc, validator) => combineErrors(acc, validator(v)), getEmptyError());

/** Валидирует поочередно и возвращает первую ошибку */
export const orderValidators =
  <V>(...funcs: Array<Validator<V>>): Validator<V> =>
  (v: V): ErrorType => {
    for (const validator of funcs) {
      const result = validator(v);

      if (result.value) {
        return result;
      }
    }

    return getEmptyError();
  };

/** Валидирует поле если выполнено условие condition */
export const validateIf =
  <T>(
    condition: (value: T) => boolean, // условие, при котором мы проходим валидацию
    validator: Validator<T>,
  ): Validator<T> =>
  (value: T): ErrorType => {
    if (condition(value)) {
      return validator(value);
    }

    return { value: false };
  };

/** Произвольный валидатор */
export const customValidate =
  <T>(condition: (v?: T) => boolean, error: string | ((v?: T) => string) = ERROR_INCORRECT_FIELD): Validator<T> =>
  (v?: T): ErrorType => {
    const errorText = typeof error === 'function' ? error(v) : error;

    return condition(v)
      ? getEmptyError()
      : {
          value: true,
          text: [errorText],
        };
  };

export const validateNumber =
  <T>(errorText: string = ERROR_ONLY_DIGITS): Validator<T> =>
  (v?: T | null): ErrorType =>
    /[^0-9]/.test(String(v))
      ? {
          value: true,
          text: [errorText],
        }
      : getEmptyError();

export const validateLetters =
  <T>(errorText: string = ERROR_ONLY_LETTERS): Validator<T> =>
  (v?: T | null): ErrorType =>
    v && /[^А-Яа-я]/.test(String(v))
      ? {
          value: true,
          text: [errorText],
        }
      : getEmptyError();

export const validateUrl =
  <T>(errorText: string = ERROR_INCORRECT_URL): Validator<T> =>
  (v?: T | null): ErrorType =>
    /^https?:\/\/[^\s/$.?#].[^\s]*$/.test(String(v))
      ? getEmptyError()
      : {
          value: true,
          text: [errorText],
        };

export const validatePhoneNumber =
  <T>(errorText: string = ERROR_PHONE): Validator<T> =>
  (v?: T | null): ErrorType => {
    if (!v) {
      return getError(errorText);
    }

    const formattedValue = String(v)
      .replace(/\s+/g, '')
      .replace(/[^\d+]/g, '');

    const phoneNumber = parsePhoneNumberFromString(formattedValue);

    return phoneNumber?.isValid() ? getEmptyError() : getError(errorText);
  };

export const validateInn =
  <T>(errorText: string = ERROR_INN): Validator<T> =>
  (v?: T | null): ErrorType => {
    return /^\d{10}(\d{2})?$/.test(String(v)) ? getEmptyError() : getError(errorText);
  };
